import { HQ } from "../sportConfig";
import React, { useEffect, useState } from "react";
import "./InstallApp.css";

const DISMISSED_AT_KEY = `${HQ.storagePrefix}-install-dismissed-at`;
const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isRecentlyDismissed = () => {
  try {
    const dismissedAt = Number((window.SportsHQSettings || window.localStorage).getItem(DISMISSED_AT_KEY));
    return dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_FOR_MS;
  } catch {
    return false;
  }
};

const platform = () => {
  const userAgent = window.navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  if (/Android/.test(userAgent)) return "android";
  return "desktop";
};

const instructions = {
  ios: [
    <>Tryck på <strong>Dela</strong> i Safaris verktygsfält.</>,
    <>Välj <strong>Lägg till på hemskärmen</strong>.</>,
    <>Tryck på <strong>Lägg till</strong>.</>,
  ],
  android: [
    <>Öppna webbläsarens meny <strong>⋮</strong>.</>,
    <>Välj <strong>Installera app</strong> eller <strong>Lägg till på startskärmen</strong>.</>,
    <>Bekräfta med <strong>Installera</strong>.</>,
  ],
  desktop: [
    <>Öppna webbläsarens meny.</>,
    <>Välj <strong>Installera Sports HQ</strong> eller installationsikonen i adressfältet.</>,
  ],
};

const InstallApp = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(isRecentlyDismissed);
  const currentPlatform = platform();

  useEffect(() => {
    const capturePrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const markInstalled = () => {
      setInstalled(true);
      setShowHelp(false);
    };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  useEffect(() => {
    if (!showHelp) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && setShowHelp(false);
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [showHelp]);

  if (installed || dismissed || currentPlatform === "desktop") return null;

  const dismiss = () => {
    try {
      (window.SportsHQSettings || window.localStorage).setItem(DISMISSED_AT_KEY, String(Date.now()));
    } catch {
      // The current session can still hide the suggestion when storage is unavailable.
    }
    setDismissed(true);
  };

  const install = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === "accepted") setInstalled(true);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <span className="install-app-action">
        <button type="button" className="install-app-button" onClick={install}>
          Installera app
        </button>
        <button type="button" className="install-app-dismiss" onClick={dismiss} aria-label="Dölj installationsförslag">×</button>
      </span>
      {showHelp && (
        <div className="install-help-backdrop" onMouseDown={() => setShowHelp(false)}>
          <section
            className="install-help"
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-help-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="install-help__close" onClick={() => setShowHelp(false)} aria-label="Stäng">×</button>
            <span className="install-help__icon" aria-hidden="true">⌂</span>
            <h2 id="install-help-title">Installera Sports HQ</h2>
            <p>Få appen på hemskärmen för snabb åtkomst.</p>
            <ol>
              {instructions[currentPlatform].map((step, index) => <li key={index}>{step}</li>)}
            </ol>
            <button type="button" className="install-help__done" onClick={() => setShowHelp(false)}>Klart</button>
          </section>
        </div>
      )}
    </>
  );
};

export default InstallApp;
